import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { FunctionComponent } from 'react';
import { Grid } from 'ui';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Typography } from 'omakase';
import WidgetLayoutContainer from 'consumer-page-widgets/WidgetUtils/WidgetLayoutContainer';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingBottom: theme.typography.pxToRem(20),
    },
    container: (props: CollectionHeroWidgetProps) => {
      let backgroundImageStyles: CSSProperties = {};
      let backgroundImageStylesMd: CSSProperties = {};
      let backgroundImageStylesLg: CSSProperties = {};
      if (props.backgroundImage) {
        backgroundImageStyles = theme.images.webPBackgroundImage(props.backgroundImage);
        backgroundImageStyles.backgroundSize = 'cover';
        backgroundImageStyles.backgroundPositon = 'center';
      }
      if (props.backgroundImageMd) {
        backgroundImageStylesMd = theme.images.webPBackgroundImage(props.backgroundImageMd);
        backgroundImageStyles.backgroundSize = 'cover';
        backgroundImageStyles.backgroundPositon = 'center';
      }
      if (props.backgroundImageLg) {
        backgroundImageStylesLg = theme.images.webPBackgroundImage(props.backgroundImageLg);
        backgroundImageStyles.backgroundSize = 'cover';
        backgroundImageStyles.backgroundPositon = 'center';
      }
      return {
        width: '100%',
        borderBottomWidth: 20,
        borderStyle: 'solid',
        paddingTop: theme.spacing(2), // offset grid negative margin
        ...backgroundImageStyles,
        [theme.breakpoints.up('md')]: {
          ...backgroundImageStylesMd,
        },
        [theme.breakpoints.up('lg')]: {
          ...backgroundImageStylesLg,
        },
      };
    },
    heroImageContainer: {
      paddingBottom: '0 !important',
      [theme.breakpoints.up('md')]: {
        paddingTop: '0 !important',
      },
    },
    heroImage: (props: CollectionHeroWidgetProps) => ({
      ...theme.images.webPBackgroundImage(props.heroImage),
      paddingBottom: theme.mediaRatios.aspectRatio(598, 494),
      backgroundPosition: 'top left',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      marginBottom: -22,
      [theme.breakpoints.up('md')]: {
        position: 'static',
        bottom: 0,
      },
    }),
    textAlignment: {
      textAlign: 'center',
      [theme.breakpoints.up('md')]: {
        textAlign: 'left',
      },
    },
    title: {
      paddingTop: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        paddingTop: 0,
      },
    },
    contentGridCell: {
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    contentContainer: {
      [theme.breakpoints.up('md')]: {
        paddingLeft: theme.spacing(5),
      },
    },
    content: {
      [theme.breakpoints.up('md')]: {
        paddingRight: theme.spacing(11),
      },
    },
  })
);

export interface CollectionHeroWidgetProps {
  /**
   * if true, the title element will be an h1
   */
  isPageTitle?: boolean;

  className?: string;
  /**
   * defaults to pepper100
   */
  primaryColor?: string;
  /**
   * defaults to pepper90
   */
  secondaryColor?: string;
  /**
   * optional, will cover the entire area behind the text, full bleed
   */
  backgroundImage?: string;
  /**
   * optional, background image for md screens
   */
  backgroundImageMd?: string;
  /**
   * optional, background image for lg screens
   */
  backgroundImageLg?: string;
  title: string;
  content: string;
  /**
   * aspect ratio of 82% expected
   */
  heroImage: string;
}

export const CollectionHeroWidget: FunctionComponent<CollectionHeroWidgetProps> = (props) => {
  const {
    className,
    content,
    primaryColor = '#2B2B2B', // pepper100 change when in theme
    secondaryColor = '#434343', // pepper90 change when in theme
    title,
    isPageTitle = false,
  } = props;
  const classes = useStyles(props);
  return (
    <section className={clsx(classes.root, className)}>
      <div
        style={{
          backgroundColor: primaryColor,
          borderBottomColor: secondaryColor,
        }}
        className={classes.container}
      >
        <WidgetLayoutContainer verticalSpacingUnits={0} verticalSpacingUnitsDesktop={0}>
          <Grid container spacing={4}>
            <Grid item className={classes.contentGridCell} xs={12} md={6}>
              <div className={classes.contentContainer}>
                <Typography
                  className={clsx(classes.textAlignment, classes.title)}
                  variant="decorative"
                  color="fgContrast"
                  mb={2}
                  component={isPageTitle ? 'h1' : 'h2'}
                  dangerouslySetInnerHTML={{ __html: title }}
                />
                <Typography
                  className={clsx(classes.textAlignment, classes.content)}
                  variant="body01"
                  color="fgContrast"
                  component="p"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </div>
            </Grid>
            <Grid className={classes.heroImageContainer} item xs={12} md={6}>
              <div className={classes.heroImage} role="img" aria-label={`${title} image`} />
            </Grid>
          </Grid>
        </WidgetLayoutContainer>
      </div>
    </section>
  );
};
